import { useCallback, useEffect, useState } from 'react';

import type { ImageModalV2ErrorToastState } from './imageModalV2ErrorToastTypes';
import type { ImageModalV2OperationToastState } from './imageModalV2OperationToastTypes';

export type ImageModalV2ModalToast =
    | { type: 'operation'; toast: ImageModalV2OperationToastState }
    | { type: 'error'; toast: ImageModalV2ErrorToastState };

export type ImageModalV2PresentedModalToast = {
    /** The toast currently mounted; lags `desiredModalToast` across a type switch until the outgoing toast finishes exiting. */
    presentedModalToast: ImageModalV2ModalToast | null;
    /** Whether the mounted toast should be showing right now, given both surface openness and desired-state agreement. */
    isOpen: boolean;
    /** Call once the mounted toast's exit transition has completed. */
    handleExitComplete: () => void;
};

/**
 * Reconciles the toast the modal *wants* to show (`desiredModalToast`, already adjusted for any
 * minimum-presentation time by the caller — see `useImageModalV2OperationToastPresentation`) against
 * the toast that is actually mounted, so a type switch (e.g. operation toast to error toast) waits for
 * the outgoing toast to finish exiting before the incoming one mounts, rather than swapping mid-motion.
 *
 * This hook does not own the bottom-surface open/exit state machine itself
 * (`useImageModalV2BottomSurfacePresentation`) — that machine is shared with other bottom surfaces
 * (file transfer, toolbars) and is driven from the caller. Instead this hook is told whether that
 * surface is currently open (`isSurfaceOpen`) and, once the presented toast has nothing left to show,
 * asks the caller to complete that surface's exit (`onSurfaceExited`).
 */
export const useImageModalV2PresentedModalToast = ({
    desiredModalToast,
    isSurfaceOpen,
    onSurfaceExited,
}: {
    desiredModalToast: ImageModalV2ModalToast | null;
    isSurfaceOpen: boolean;
    onSurfaceExited: () => void;
}): ImageModalV2PresentedModalToast => {
    const [presentedModalToast, setPresentedModalToast] = useState<ImageModalV2ModalToast | null>(desiredModalToast);

    useEffect(() => {
        setPresentedModalToast((currentToast) => {
            if (!desiredModalToast) return currentToast;
            if (!currentToast || currentToast.type === desiredModalToast.type) return desiredModalToast;
            return currentToast;
        });
    }, [desiredModalToast]);

    const handleExitComplete = useCallback(() => {
        if (isSurfaceOpen && desiredModalToast) {
            setPresentedModalToast(desiredModalToast);
            return;
        }

        setPresentedModalToast(null);
        onSurfaceExited();
    }, [desiredModalToast, isSurfaceOpen, onSurfaceExited]);

    const isOpen = isSurfaceOpen && desiredModalToast?.type === presentedModalToast?.type;

    return { presentedModalToast, isOpen, handleExitComplete };
};
